import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from './shared/Button';
import { DownloadSection } from './shared/DownloadSection';
import { PaymentData } from '../types/PaymentData';
import { telHref } from '../splitReceipt/model';
import { paymentDial, vendorLabel } from './paymentLink';

export const PaymentScreen: React.FC = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const paymentData = location.state as PaymentData;

    // Payment-method picker UI was removed; Mobile Money is the only option.
    const selectedPayment = 'Mobile Money';

    // Redirect if no payment data (e.g. direct navigation without a token)
    useEffect(() => {
        if (!paymentData) {
            navigate('/');
        }
    }, [paymentData, navigate]);

    const handlePayment = () => {
        if (selectedPayment) {
            // Opens the dialer with the MoMo code: the vendor's merchant code, else the sender's number.
            window.location.href = telHref(paymentDial(paymentData));
        }
    };

    if (!paymentData) {
        return <div>Loading...</div>;
    }

    // Format amount with currency
    const formatAmount = (amount: number, currency: string) => {
        return `${currency} ${amount.toLocaleString()}`;
    };

    const vendor = paymentData.destination;

    return (
        <div style={{ padding: '20px', maxWidth: '400px', margin: '0 auto' }}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '24px' }}>
                <img
                    src="/images/icons/pe.svg"
                    alt="Logo"
                    style={{
                        width: '40px',
                        height: '40px',
                    }}
                />
            </div>

            {/* Info of the sender of the link */}
            <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                marginBottom: '24px',
                padding: '24px 16px',
                backgroundColor: '#f9fafb',
                borderRadius: '12px',
                textAlign: 'center'
            }}>
                <div style={{
                    width: '56px',
                    height: '56px',
                    backgroundColor: '#dc2626',
                    borderRadius: '50%',
                    marginBottom: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontSize: '18px',
                    fontWeight: 'bold',
                    letterSpacing: '1px'
                }}>
                    {paymentData.sender.name
                        .split(' ')
                        .map((n: string) => n[0])
                        .slice(0, 2)
                        .join('')
                        .toUpperCase()}
                </div>
                <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '4px' }}>
                    {vendor
                        ? `${paymentData.sender.name} split a bill at ${vendorLabel(vendor)}`
                        : `${paymentData.sender.name} is requesting`}
                </div>
                <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#111827' }}>
                    {formatAmount(paymentData.amount, paymentData.currency)}
                </div>
                {vendor && (
                    <div style={{ fontSize: '13px', color: '#6b7280', marginTop: '2px' }}>Your share</div>
                )}
                <div style={{ marginTop: '12px', borderTop: '1px solid #e5e7eb', paddingTop: '12px', display: 'flex', alignItems: 'center', gap: '8px', color: '#374151', fontSize: '14px' }}>
                    <span>{vendor ? '🏪' : '📞'}</span>
                    <span>
                        {vendor
                            ? `Paying ${vendorLabel(vendor)} · code ${vendor.code}`
                            : `Paying ${paymentData.sender.phoneNumber}`}
                    </span>
                </div>
            </div>

            {/* Disclaimer */}
            <div style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '10px',
                padding: '12px 16px',
                backgroundColor: '#fffbeb',
                border: '1px solid #f59e0b',
                borderRadius: '8px',
                marginBottom: '24px',
                fontSize: '13px',
                color: '#92400e'
            }}>
                <span style={{ flexShrink: 0, marginTop: '1px' }}>⚠️</span>
                <span>Check that the recipient's name matches who you intend to pay before sending.</span>
            </div>

            {/* Pay Button */}
            <Button
                onClick={handlePayment}
                disabled={!selectedPayment}
            >
                Pay {formatAmount(paymentData.amount, paymentData.currency)}
            </Button>

            {/* Security Notice */}
            <div style={{
                textAlign: 'center',
                margin: '16px 0',
                fontSize: '12px',
                color: '#6b7280'
            }}>
                🔒 POWERED BY PEBLA
            </div>

            {/* App Download Section */}
            <DownloadSection />
        </div>
    );
};